/**
 * Pure view-model helpers for submission comment threads.
 *
 * Intentionally dependency-free so they are trivially unit-testable.
 */

/**
 * Sort a thread's messages into chronological (ascending) order.
 * Returns a new array; the input is not mutated.
 */
export function sortThread<T extends { created_at: string }>(messages: T[]): T[] {
  return [...messages].sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/**
 * Newest message in a thread, or null for an empty thread.
 *
 * A message is timed at `published_at ?? created_at`, the moment the vendor
 * first saw it. That is publish time for an admin comment and creation time for
 * a vendor comment, which has no `published_at`. Keying on `created_at` alone
 * would get one sequence wrong: draft at T1, vendor reply at T2, publish at T3.
 * The reviewer's comment is the vendor's newest message, but by `created_at` it
 * would rank as the oldest. This deliberately does not reuse `sortThread`,
 * which keys on `created_at` for the thread's display order. A tie goes to the
 * last message in input order, the same rule `currentRoundLatestAuthor` uses.
 */
export function latestThreadMessage<T extends { created_at: string; published_at?: string | null }>(
  messages: readonly T[],
): T | null {
  let latest: T | null = null;
  for (const m of messages) {
    if (latest === null || visibleAt(m) >= visibleAt(latest)) latest = m;
  }
  return latest;
}

/** When the vendor first saw a message: publish time for an admin comment, creation time for a vendor one. */
export function visibleAt(m: { created_at: string; published_at?: string | null }): string {
  return m.published_at ?? m.created_at;
}

/**
 * Preview text for a message. Returns the trimmed body when it has one. For a
 * message with only attachments it returns "1 attachment" or "N attachments".
 * A removal-only reply reads "Removed 1 document" or "Removed N documents", and
 * a message with none of these returns ''.
 */
export function messagePreviewText(c: {
  body: string | null;
  attachments: readonly unknown[];
  removed_files?: readonly unknown[];
}): string {
  const body = c.body?.trim() ?? '';
  if (body) return body;
  const n = c.attachments.length;
  if (n > 0) return n === 1 ? '1 attachment' : `${n} attachments`;
  const removed = c.removed_files?.length ?? 0;
  if (removed === 0) return '';
  return removed === 1 ? 'Removed 1 document' : `Removed ${removed} documents`;
}

/**
 * Label shown under a comment's attachment. Assumes an attachment exists.
 * Answer attachments (vendor upload, or admin replace) read as the document;
 * an admin's non-answer attachment is a reference example.
 */
export function attachmentLabel(c: {
  author_type: 'admin' | 'vendor';
  attachment_is_answer: boolean;
}): string {
  if (c.attachment_is_answer) return 'Uploaded as the document';
  if (c.author_type === 'admin') return 'Example requested by admin — reference only';
  return 'Attachment';
}
